import { ResizedImage, Speaker as SpeakerProps } from '@clipr/lib';
import { makeObservable, computed } from 'mobx';
import { Store } from '../store/store';
import { StoreNode } from '../store';
import { IJobDependency } from './jobSchema';
import { SpeakerPrediction } from './speakers/speakerPrediction';

export const SCOPE_TEAM_PREFIX = `TEAM#`;
export const SCOPE_JOB_PREFIX = `JOB#`;

export type Speaker = SpeakerModel;
export type { Speaker as SpeakerProps } from '@clipr/lib';

export class SpeakerModel
  extends StoreNode
  implements IJobDependency {

  constructor(props: SpeakerProps, store: Store) {
    super(store);
    makeObservable(this, {
      displayName: computed,
      pictureURL: computed,
      isPublic: computed,
      isPrivate: computed,
      hasPublicScope: computed,
      hasTeamScope: computed,
      hasJobScope: computed,
      matchPercentage: computed,
      matchPercentageDisplay: computed,
      hasMatch: computed,
      isConfirmed: computed,
      matchId: computed,
    });

    Object.assign(this, props);

    if (props.prediction) {
      this.prediction = new SpeakerPrediction(props.prediction, this.store)
    }
  }

  readonly nodeType: 'Speaker' = 'Speaker';

  declare readonly id: string;
  readonly jobId!: string;
  readonly name!: string;
  
  readonly description!: string;
  readonly linkedin!: string;
  readonly picture!: ResizedImage[];
  readonly scope!: string | null;
  readonly isNonSpeech!: boolean | null;
  readonly prediction: SpeakerPrediction | null = null;

  get displayName() {
    return this.name;
  }

  get pictureURL(): string | null {
    return (this.picture &&
      this.picture.length > 0 &&
      this.picture[0]?.url) || null;
  }

  get isPublic(): boolean {
    return this.hasPublicScope || this.hasTeamScope;
  }

  get isPrivate(): boolean {
    return this.hasJobScope;
  }

  get hasPublicScope(): boolean {
    return this.scope === 'public';
  }

  get hasTeamScope(): boolean {
    return !!this.scope?.startsWith(SCOPE_TEAM_PREFIX);
  }

  get hasJobScope(): boolean {
    return !!this.scope?.startsWith(SCOPE_JOB_PREFIX);
  }

  get matchPercentage(): number {
    return this.prediction?.matchPercentage ?? 0;
  }

  get matchPercentageDisplay(): string | null {
    return this.prediction?.matchPercentageDisplay ?? null;
  }

  get hasMatch(): boolean {
    return this.prediction?.hasMatch ?? false;
  }

  get isConfirmed(): boolean {
    // at this a lack of prediction means the speaker is confirmed
    return this.prediction?.isConfirmed ?? true;
  }

  get matchId(): string | null {
    return this.prediction?.speakerId ?? null;
  }
}
